import * as React from 'react'
import { useRouter } from 'next/router'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Alert from '@material-ui/lab/Alert'
import clsx from 'clsx'
import { useGlobal } from 'api'
import { useApp } from 'containers/App/AppContext'
import EmailIcon from 'components/icons/Email'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import FacebookButton from 'components/FacebookButton'
import FormControlLabel from 'components/FormControlLabel'
import GoogleButton from 'components/GoogleButton'
import Link from 'components/Link'
import Typography from 'components/Typography'
import { useTranslations } from 'containers/Translations/TranslationsContext'
import getConfig from 'utils/getConfig'
import { useAuthHandlers } from '../AuthContext'
import AuthSocialButton from './AuthSocialButton'

const { publicRuntimeConfig } = getConfig() || {}

const googleClientId = publicRuntimeConfig.GOOGLE_CLIENT_ID

export const useStyles = makeStyles((theme) => ({
  header: {},
  main: {},
  footer: {},
  button: theme.mixins.inputMargin('dense'),
  buttonDisabled: {
    opacity: 0.25,
    pointerEvents: 'none',
  },
  consent: {
    marginTop: theme.spacing(2),
  },
  alert: { marginTop: theme.spacing(1) },
  googleButtonCustom: {
    '& > div ': {
      width: 120,
      display: 'flex',
      justifyContent: 'center',
    },
  },
}))

function AuthRegister(props) {
  const classes = useStyles(props)

  const router = useRouter()
  const { isNavMenuOpen, onLoginDialogClose } = useApp()
  const { settings } = useGlobal()
  const {
    onLoginOrRegisterScreenToggle,
    onRegisterEmailScreenOpen,
    onLoginWithFacebook,
    onLoginWithGoogle,
    onSocialLoginFailure,
  } = useAuthHandlers()

  const [isConsented, setConsented] = React.useState(false)
  const [showConsentError, setShowConsentError] = React.useState(false)
  const [socialError, setSocialError] = React.useState(null)
  const t = useTranslations()

  const handleSocialLoginFailure = React.useCallback(
    (error) => {
      const messages = onSocialLoginFailure(error)

      setSocialError(messages)
    },
    [onSocialLoginFailure],
  )

  const handleSocialLogin = React.useCallback(
    async (result, provider) => {
      try {
        if (provider === 'facebook') {
          await onLoginWithFacebook(result)
        } else if (provider === 'google') {
          await onLoginWithGoogle(result)
        }

        if (isNavMenuOpen && router?.pathname !== '/checkout') {
          router?.push('/account/')
        }
        onLoginDialogClose()
      } catch (error) {
        handleSocialLoginFailure(error)
      }
    },
    [
      onLoginWithFacebook,
      onLoginWithGoogle,
      isNavMenuOpen,
      router,
      onLoginDialogClose,
      handleSocialLoginFailure,
    ],
  )

  // const buttonClassName = clsx(classes.button, {
  //   // [classes.buttonDisabled]: !isConsented,
  // })

  const handleConsentChange = React.useCallback((event, value) => {
    setConsented(value)
    setShowConsentError(!value)
  }, [])

  const handleNoConsentButtonClick = React.useCallback(() => {
    setShowConsentError(true)
  }, [])

  const buttonProps = {}
  if (!isConsented) {
    buttonProps.onClick = handleNoConsentButtonClick // Override `onClick` if not consented.
  }

  return (
    <>
      <div className={classes.header}>
        <Typography component="h1" variant="h5">
          {t('Web.AuthRegister.SignUp')}
        </Typography>

        <Typography>{t('Web.AuthRegister.NoAccount')}</Typography>
      </div>

      <div className={classes.main}>
        <Button
          className={classes.button}
          onClick={onRegisterEmailScreenOpen}
          startIcon={<EmailIcon />}
          variant="outlined"
          fullWidth
          {...buttonProps}
        >
          {t('Web.AuthRegister.Email')}
        </Button>

        {publicRuntimeConfig.FACEBOOK_APP_ID && (
          <AuthSocialButton
            className={classes.button}
            component={FacebookButton}
            provider="facebook"
            appId={publicRuntimeConfig.FACEBOOK_APP_ID}
            onSuccess={handleSocialLogin}
            onError={handleSocialLoginFailure}
            fullWidth
            {...buttonProps}
          >
            {t('Web.AuthRegister.Facebook')}
          </AuthSocialButton>
        )}

        <AuthSocialButton
          component={GoogleButton}
          className={clsx(classes.button, classes.googleButtonCustom)}
          provider="google"
          appId={googleClientId}
          onSuccess={handleSocialLogin}
          onError={handleSocialLoginFailure}
          id="g_id_signin"
          fullWidth
          {...buttonProps}
        >
          {t('Web.AuthRegister.Google')}
        </AuthSocialButton>

        <FormControlLabel
          className={classes.consent}
          control={<Checkbox required />}
          onChange={handleConsentChange}
          value={isConsented}
          label={
            <>
              {t('Web.AuthRegister.Acceptance')}{' '}
              <Link
                href={settings.termsPageUrl}
                target="_blank"
                underline="always"
                variant="inherit"
              >
                {t('Web.AuthRegister.AcceptancePersonalPolicy')}
              </Link>
              .
            </>
          }
        />

        {showConsentError && (
          <Alert className={classes.alert} severity="error">
            {t('Web.AuthRegister.NeedToAccept')}
          </Alert>
        )}

        {socialError && (
          <Alert className={classes.alert} severity="error">
            {socialError?.map((error, idx) => (
              <div key={idx}>{error}</div>
            ))}
          </Alert>
        )}
      </div>

      <div className={classes.footer}>
        <Typography>{t('Web.AuthRegister.AlreadyHaveAccount')}</Typography>

        <Link // eslint-disable-line jsx-a11y/anchor-is-valid
          onClick={onLoginOrRegisterScreenToggle}
          component="button"
          type="button"
          underline="always"
          variant="body1"
        >
          {t('Web.AuthRegister.SignIn')}
        </Link>
      </div>
    </>
  )
}

export default AuthRegister
